import React from "react";
import clsx from "clsx";

export type DialogButtonProps = {
  show?: boolean;
  text?: string;
  disabled?: boolean;
  onClick?: () => void;
};

export type DialogProps = {
  show: boolean;
  message: React.ReactNode;
  messageClassName?: string;
  className?: string;
  width?: string;
  showBorder?: boolean;
  confirmButton?: DialogButtonProps;
  cancelButton?: DialogButtonProps;
  onDismiss?: () => void;
};

export function Dialog({
  show,
  message,
  messageClassName = "",
  className = "",
  width = "368px",
  showBorder = true,
  confirmButton,
  cancelButton,
  onDismiss,
}: DialogProps) {
  const confirmConfig = React.useMemo(
    () => ({
      show: confirmButton?.show ?? true,
      text: confirmButton?.text,
      disabled: confirmButton?.disabled ?? false,
      onClick: confirmButton?.onClick,
    }),
    [confirmButton],
  );

  const cancelConfig = React.useMemo(
    () => ({
      show: cancelButton?.show ?? false,
      text: cancelButton?.text,
      disabled: cancelButton?.disabled ?? false,
      onClick: cancelButton?.onClick ?? onDismiss,
    }),
    [cancelButton, onDismiss],
  );

  const dialogElement = React.useRef<HTMLDialogElement>(null);

  React.useEffect(() => {
    const dialog = dialogElement.current;
    const handleClose = cancelConfig.onClick;

    if (!dialog || !handleClose) return;

    dialog.addEventListener("close", handleClose);
    return () => {
      dialog.removeEventListener("close", handleClose);
    };
  }, [cancelConfig.onClick]);

  React.useEffect(() => {
    const dialog = dialogElement.current;
    if (!dialog) return;

    if (show) {
      dialog.showModal();
    } else {
      dialog.close();
    }
  }, [show]);

  React.useEffect(() => {
    const dialog = dialogElement.current;
    const handleCancel = cancelConfig.onClick;

    if (!dialog || !handleCancel) return;

    const handleClick = (event: MouseEvent) => {
      if (event.target === dialog) {
        handleCancel();
      }
    };

    dialog.addEventListener("mousedown", handleClick);
    return () => {
      dialog.removeEventListener("mousedown", handleClick);
    };
  }, [cancelConfig.onClick]);

  return (
    <dialog
      ref={dialogElement}
      className={clsx(
        "m-auto rounded-2xl ts-dialog items-center justify-between min-h-0 shadow-lg bg-pureWhite",
        `w-[${width}]`,
        className,
      )}
    >
      <div className="flex flex-col p-6 gap-2.5">
        <div
          className={clsx(
            "w-full bg-pureWhite flex flex-col gap-2 justify-center text-feintGray text-sm rounded-lg",
            showBorder && "border border-lightGray",
            messageClassName,
          )}
        >
          {message}
        </div>
        <div className="w-full bg-pureWhite flex flex-col gap-2">
          {cancelConfig.show && (
            <button
              className={clsx(
                "rounded-lg px-4 py-2 w-full border border-lightGray text-feintGray",
                cancelConfig.disabled ? "opacity-20 cursor-not-allowed" : "hover:bg-gray-50",
              )}
              onClick={() => {
                cancelConfig.onClick?.();
                dialogElement.current?.close();
              }}
              disabled={cancelConfig.disabled}
            >
              {cancelConfig.text}
            </button>
          )}
          {confirmConfig.show && (
            <button
              className={clsx(
                "rounded-lg px-4 py-2 w-full bg-primaryColor text-white",
                confirmConfig.disabled
                  ? "opacity-20 cursor-not-allowed"
                  : "hover:bg-primaryColor/90",
              )}
              onClick={() => {
                confirmConfig.onClick?.();
                dialogElement.current?.close();
              }}
              disabled={confirmConfig.disabled}
            >
              {confirmConfig.text}
            </button>
          )}
        </div>
      </div>
    </dialog>
  );
}
